import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Download } from "lucide-react";
import Layout from "../components/Layout";
import { api } from "../lib/api";

function fmtMonth(s) {
  if (!s) return s;
  try {
    const [y, m] = s.split("-");
    return new Date(Number(y), Number(m) - 1, 1).toLocaleString("default", { month: "short", year: "numeric" });
  } catch {
    return s;
  }
}

function downloadCsv(filename, rows) {
  if (!rows || rows.length === 0) return;
  const cols = Array.from(rows.reduce((acc, r) => { Object.keys(r).forEach((k) => acc.add(k)); return acc; }, new Set()));
  const esc = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const csv = [cols.join(","), ...rows.map((r) => cols.map((c) => esc(r[c])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${filename}.csv`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 1000);
}

export default function ReportsQualifiedDrilldown() {
  const { month, mode } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState({ agents: [], stages: [], cells: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/reports/qualified-leads-drilldown?month=${encodeURIComponent(month)}&mode=${encodeURIComponent(mode)}`);
        setData(data || { agents: [], stages: [], cells: [], total: 0 });
      } finally {
        setLoading(false);
      }
    })();
  }, [month, mode]);

  // O(1) lookup for cells
  const cellMap = useMemo(() => {
    const m = new Map();
    (data.cells || []).forEach((c) => m.set(`${c.agent_id}__${c.stage}`, c));
    return m;
  }, [data]);

  // Row/column totals
  const rowTotals = useMemo(() => {
    const t = {};
    (data.cells || []).forEach((c) => { t[c.agent_id] = (t[c.agent_id] || 0) + c.count; });
    return t;
  }, [data]);

  const colTotals = useMemo(() => {
    const t = {};
    (data.cells || []).forEach((c) => { t[c.stage] = (t[c.stage] || 0) + c.count; });
    return t;
  }, [data]);

  const grandTotal = data.total || 0;

  const buildLeadsUrl = (agentId, stage) => {
    const sp = new URLSearchParams();
    sp.set("qualified_month", month);
    sp.set("qualified_mode", mode);
    if (agentId && agentId !== "__unassigned__") sp.set("lead_owner_id", agentId);
    if (stage) sp.set("lead_stage", stage);
    return `/leads?${sp.toString()}`;
  };

  const modeLabel = mode === "same_month" ? "Same-Month" : "Any-Month";

  return (
    <Layout
      title={`Qualified leads · ${modeLabel} · ${fmtMonth(month)}`}
      breadcrumb={["Reports", "Qualified drilldown"]}
    >
      <div className="mb-4 flex items-center justify-between">
        <button
          onClick={() => navigate("/reports")}
          className="text-sm font-medium text-slate-600 hover:text-orange-600 inline-flex items-center gap-1.5"
          data-testid="back-to-reports"
        >
          <ArrowLeft size={14} /> Back to Reports
        </button>
        <button
          data-testid="download-drilldown"
          onClick={() => {
            const rows = (data.agents || []).map((a) => {
              const row = { agent: a.name };
              (data.stages || []).forEach((s) => { row[s] = cellMap.get(`${a.id}__${s}`)?.count || 0; });
              row.total = rowTotals[a.id] || 0;
              return row;
            });
            downloadCsv(`qualified-${mode}-${month}`, rows);
          }}
          className="text-sm font-medium px-3 py-1.5 rounded-md bg-orange-500 hover:bg-orange-600 text-white inline-flex items-center gap-1.5"
        >
          <Download size={14} /> Download CSV
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-base font-semibold text-slate-900">
            {modeLabel} Qualified · {fmtMonth(month)}
          </h3>
          <div className="text-xs text-slate-500">
            {loading ? "Loading…" : (
              <span data-testid="drilldown-total">
                <b>{grandTotal}</b> qualified lead{grandTotal === 1 ? "" : "s"} · click any number to open the leads
              </span>
            )}
          </div>
        </div>

        <div className="text-xs text-slate-500 mb-3">
          {mode === "same_month" ? (
            <>Bifurcation of leads that were <b>created in {fmtMonth(month)}</b> AND first-qualified in {fmtMonth(month)}. Grouped by their <em>current</em> owner and stage.</>
          ) : (
            <>Bifurcation of leads that were <b>first-qualified in {fmtMonth(month)}</b> (regardless of when they were created). Grouped by their <em>current</em> owner and stage.</>
          )}
        </div>

        {loading ? (
          <div className="text-center py-10 text-sm text-slate-500">Loading…</div>
        ) : (data.agents || []).length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-500">
            No qualified leads in this scope.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="drilldown-table">
              <thead>
                <tr>
                  <th className="sticky left-0 bg-white text-left py-2 pr-4 text-xs uppercase tracking-wider text-slate-500 font-medium">Agent</th>
                  {(data.stages || []).map((s) => (
                    <th key={s} className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-500 font-medium whitespace-nowrap">{s}</th>
                  ))}
                  <th className="text-right py-2 px-3 text-xs uppercase tracking-wider text-slate-600 font-semibold whitespace-nowrap border-l border-slate-200">Total</th>
                </tr>
              </thead>
              <tbody>
                {(data.agents || []).map((a) => (
                  <tr key={a.id} className="border-t border-slate-100" data-testid={`drilldown-row-${a.id}`}>
                    <td className="sticky left-0 bg-white py-2 pr-4 font-medium text-slate-800 whitespace-nowrap">{a.name}</td>
                    {(data.stages || []).map((s) => {
                      const cell = cellMap.get(`${a.id}__${s}`);
                      const n = cell?.count || 0;
                      return (
                        <td key={s} className="text-right py-2 px-3 tabular-nums">
                          {n > 0 ? (
                            <Link
                              to={buildLeadsUrl(a.id, s)}
                              className="text-emerald-700 font-semibold hover:text-emerald-900 hover:underline"
                              data-testid={`drilldown-cell-${a.id}-${s}`}
                            >
                              {n}
                            </Link>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="text-right py-2 px-3 tabular-nums font-semibold text-slate-800 border-l border-slate-200">
                      <Link
                        to={buildLeadsUrl(a.id, null)}
                        className="text-slate-800 hover:text-orange-600 hover:underline"
                        data-testid={`drilldown-agent-total-${a.id}`}
                      >
                        {rowTotals[a.id] || 0}
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-300 bg-slate-50 font-semibold" data-testid="drilldown-totals-row">
                  <td className="sticky left-0 bg-slate-50 py-2 pr-4 uppercase text-xs tracking-wider text-slate-600">Total</td>
                  {(data.stages || []).map((s) => (
                    <td key={s} className="text-right py-2 px-3 tabular-nums text-slate-800">
                      {colTotals[s] ? (
                        <Link
                          to={buildLeadsUrl(null, s)}
                          className="text-slate-800 hover:text-orange-600 hover:underline"
                          data-testid={`drilldown-stage-total-${s}`}
                        >
                          {colTotals[s]}
                        </Link>
                      ) : "—"}
                    </td>
                  ))}
                  <td className="text-right py-2 px-3 tabular-nums text-slate-900 border-l border-slate-200">
                    <Link
                      to={buildLeadsUrl(null, null)}
                      className="text-slate-900 hover:text-orange-600 hover:underline"
                      data-testid="drilldown-grand-total"
                    >
                      {grandTotal}
                    </Link>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
